import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase';
import './StatsCards.css';

const StatsCards = () => {
  const [stats, setStats] = useState([
    {
      icon: '📦',
      number: '0',
      label: 'Orders Today',
      growth: '+0%',
      iconBg: '#feecec',
      iconColor: '#fe0000'
    },
    {
      icon: '💰',
      number: 'Rs. 0',
      label: "Today's Revenue",
      growth: '+0%',
      iconBg: '#feecec',
      iconColor: '#fe0000'
    },
    {
      icon: '👥',
      number: '0',
      label: 'Active Customers',
      growth: '+0%',
      iconBg: '#feecec',
      iconColor: '#fe0000'
    },
    {
      icon: '⭐',
      number: '0.0',
      label: 'Average Rating',
      growth: '+0.0',
      iconBg: '#FFF9E6',
      iconColor: '#F7B801'
    }
  ]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0, 0);
      const todayStart = today.toISOString();
      const todayEnd = new Date(today);
      todayEnd.setHours(23, 59, 59, 999);
      const todayEndISO = todayEnd.toISOString();

      // Fetch orders today
      const { count: ordersTodayCount } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', todayStart)
        .lte('created_at', todayEndISO);

      // Fetch revenue today
      const { data: ordersToday } = await supabase
        .from('orders')
        .select('total')
        .gte('created_at', todayStart)
        .lte('created_at', todayEndISO);

      const revenueToday = (ordersToday || []).reduce((sum, order) => sum + parseFloat(order.total || 0), 0);

      // Fetch orders yesterday for comparison
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStart = yesterday.toISOString();
      const yesterdayEnd = new Date(yesterday);
      yesterdayEnd.setHours(23, 59, 59, 999);
      const yesterdayEndISO = yesterdayEnd.toISOString();

      const { count: ordersYesterdayCount } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', yesterdayStart)
        .lte('created_at', yesterdayEndISO);

      const { data: ordersYesterday } = await supabase
        .from('orders')
        .select('total')
        .gte('created_at', yesterdayStart)
        .lte('created_at', yesterdayEndISO);

      const revenueYesterday = (ordersYesterday || []).reduce((sum, order) => sum + parseFloat(order.total || 0), 0);

      // Calculate growth percentages
      const ordersGrowth = ordersYesterdayCount > 0 
        ? (((ordersTodayCount || 0) - ordersYesterdayCount) / ordersYesterdayCount * 100).toFixed(0)
        : 0;
      const revenueGrowth = revenueYesterday > 0
        ? (((revenueToday - revenueYesterday) / revenueYesterday) * 100).toFixed(0)
        : 0;

      // Fetch active customers (customers with orders in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: recentOrders } = await supabase
        .from('orders')
        .select('customer_id')
        .gte('created_at', thirtyDaysAgo.toISOString());

      const activeCustomerIds = new Set(
        (recentOrders || [])
          .map(order => order.customer_id)
          .filter(id => id !== null)
      );

      // For average rating, we'll set it to 0 for now (can be calculated from reviews if you have a reviews table)
      const avgRating = 0.0;
      const ratingGrowth = 0.0;

      setStats([
        {
          icon: '📦',
          number: (ordersTodayCount || 0).toString(),
          label: 'Orders Today',
          growth: `${ordersGrowth >= 0 ? '+' : ''}${ordersGrowth}%`,
          iconBg: '#feecec',
          iconColor: '#fe0000'
        },
        {
          icon: '💰',
          number: `Rs. ${revenueToday.toLocaleString()}`,
          label: "Today's Revenue",
          growth: `${revenueGrowth >= 0 ? '+' : ''}${revenueGrowth}%`,
          iconBg: '#feecec',
          iconColor: '#fe0000'
        },
        {
          icon: '👥',
          number: activeCustomerIds.size.toString(),
          label: 'Active Customers',
          growth: '+0%',
          iconBg: '#feecec',
          iconColor: '#fe0000'
        },
        {
          icon: '⭐',
          number: avgRating.toFixed(1),
          label: 'Average Rating',
          growth: `${ratingGrowth >= 0 ? '+' : ''}${ratingGrowth.toFixed(1)}`,
          iconBg: '#FFF9E6',
          iconColor: '#F7B801'
        }
      ]);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  return (
    <div className="stats-cards">
      {stats.map((stat, index) => (
        <div key={index} className="stat-card">
          <div 
            className="stat-icon-wrapper"
            style={{ 
              backgroundColor: stat.iconBg,
              color: stat.iconColor
            }}
          >
            <span className="stat-icon">{stat.icon}</span>
          </div>
          <div className="stat-content">
            <div className="stat-number">{stat.number}</div>
            <div className="stat-label">{stat.label}</div>
            <div className="stat-growth">
              <span className="growth-arrow">↑</span>
              <span className="growth-text">{stat.growth}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatsCards;

